import useCreateTask from '@/api/mutations/tasks/useCreateTask';
import useUpdateChecklistTask from '@/api/mutations/tasks/useUpdateChecklistTask';
import useUpdateTask from '@/api/mutations/tasks/useUpdateTask';
import {
  CreateTaskConfigInput,
  CreateTaskInput,
  TaskLinkedToInput,
  UpdateTaskConfigInput,
  UpdateTaskInput,
} from '@/gql/graphql';
import { CreateEditTaskFormValues } from '@/ui/components/createEditTaskSidebar/types';

export const useUpdateTaskMutation = () => {
  /**
   * mutations
   */
  const { mutateAsync: updateTaskMutation } = useUpdateTask();
  const { mutateAsync: updateChecklistTaskMutation } = useUpdateChecklistTask();

  return async (
    data: CreateEditTaskFormValues,
    cfg: {
      taskPublicId?: string;
      originalData?: CreateEditTaskFormValues;
    } = {}
  ) => {
    let config: UpdateTaskConfigInput | undefined = undefined;
    let newChecklistItems: { publicId?: string; label: string }[] = [],
      updatedChecklistItems: { publicId: string; label: string }[] = [],
      deletedChecklistItems: { publicId: string; label: string }[] = [];
    if (data.type === 'checklist') {
      newChecklistItems = data.checklistItems.filter((item) => !item.publicId);

      updatedChecklistItems = data.checklistItems.filter(
        (item) => !!item.publicId
      ) as { publicId: string; label: string }[];

      deletedChecklistItems =
        cfg.originalData?.type === 'checklist'
          ? (cfg.originalData?.checklistItems.filter((originalItem) => {
              const itemIsStillInNewDataObject = !!data.checklistItems.find(
                (item) => item.publicId === originalItem.publicId
              );

              return !!originalItem.publicId && !itemIsStillInNewDataObject;
            }) as { publicId: string; label: string }[])
          : [];

      config = {
        checklist: {
          name: data.name,
        },
      };
    }

    if (!cfg.taskPublicId) {
      throw new Error('Bad form data');
    }

    const input: UpdateTaskInput = {
      taskId: cfg.taskPublicId,
      assignedToUserId: data.assignedTo,
      deadlineDate: data.dueDate?.toISOString(),
      notes: data.notes,
      config,
    };

    if (data.type === 'checklist') {
      const response = await updateChecklistTaskMutation({
        input,
        newChecklistItems: {
          taskId: cfg.taskPublicId,
          checklistItems: newChecklistItems.map((i) => i.label),
        },
        updateChecklistItems: updatedChecklistItems.map((i) => ({
          checklistItemId: i.publicId,
          name: i.label,
        })),
        deleteChecklistItems: {
          checklistItemIds: deletedChecklistItems.map((i) => i.publicId),
        },
      });

      return response.updateTask.id;
    } else {
      const response = await updateTaskMutation(input);

      return response.updateTask.id;
    }
  };
};

export const useCreateTaskMutation = () => {
  /**
   * mutations
   */
  const { mutateAsync: createTaskMutation } = useCreateTask();

  return async (
    data: CreateEditTaskFormValues,
    cfg: { scheduledEventPublicId?: string; projectPublicId?: string } = {}
  ) => {
    let linkedTo: TaskLinkedToInput | undefined = undefined;
    if (cfg.scheduledEventPublicId) {
      linkedTo = { eventId: cfg.scheduledEventPublicId };
    } else if (cfg.projectPublicId) {
      linkedTo = { projectId: cfg.projectPublicId };
    }

    let config: CreateTaskConfigInput | undefined = undefined;
    if (data.type === 'checklist') {
      config = {
        checklist: {
          name: data.name,
          checklistItems: data.checklistItems.map((item) => item.label),
        },
      };
    }

    if (data.type === 'form') {
      if (typeof data.formId !== 'number') {
        throw new Error('Bad form data');
      }

      config = {
        form: {
          formTemplateId: data.formId.toString(),
        },
      };
    }

    if (data.type === 'custom-fields') {
      if (!data.customFieldIdentifier || !data.customFieldGroupId) {
        throw new Error('Bad form data');
      }

      config = {
        customFields: {
          customFieldGroupId: data.customFieldGroupId,
          customFieldIdentifiers: [data.customFieldIdentifier],
        },
      };
    }

    if (!config) {
      throw new Error('Bad form data');
    }

    const input: CreateTaskInput = {
      type: data.type,
      assignedToUserId: data.assignedTo,
      deadlineDate: data.dueDate?.toISOString(),
      notes: data.notes,
      linkedTo,
      config,
    };

    const response = await createTaskMutation(input);

    return response.createTask.id;
  };
};
